import React, { ReactElement, useEffect } from "react";
import { useRouter } from "next/router";
import { identify, logEvent } from "../../analytics";
import { useWebTranslation } from "../../translations";
import { Layout } from "../../components/PageTemplates/Layout";
import client from "../../../apollo-client";
import { gql } from "@apollo/client";
import { GetServerSideProps } from "next";
import { breedToSpecies } from "../../utils/insurance";
import { GetBreedsSignupIndex } from "../../../__generated__/GetBreedsSignupIndex";
import { resumePreviewStore } from "components/Signup/flow/utils/previewStore";
import { stateStore } from "components/Signup/flow/utils/stateStore";
import { applyStateFromQuery } from "components/Signup/flow/utils/applyStateFromQuery";
import { getInitialState } from "components/Signup/store/state";
import { trackAppSession } from "components/Signup/analytics/track";
import { MinimalBreedInfo } from "components/Signup/store/state/types";

export default function Index({
  breeds,
}: {
  breeds: MinimalBreedInfo[];
}): ReactElement {
  const { t } = useWebTranslation();
  const router = useRouter();

  useEffect(() => {
    (async () => {
      if (router.query.inApp === "true") {
        trackAppSession();
      }

      resumePreviewStore.remove();

      const state = applyStateFromQuery(
        router.query,
        getInitialState(),
        breeds
      );

      state.metadata.fromApp = !!router.query.inApp;

      stateStore.save(state);

      await identify(undefined);
      await logEvent("INSURANCE_SIGNUP_STARTED", {
        fromApp: !!router.query.inApp,
      });

      // Preserve `utm_*` query parameters for tracking (affiliate) purposes
      const filteredQuery = Object.keys(router.query)
        .filter((key) => key.startsWith("utm_"))
        .reduce<Record<string, string>>((obj, key) => {
          const qp = router.query[key];

          if (!qp || Array.isArray(qp)) {
            return obj;
          }

          obj[key] = qp;

          return obj;
        }, {});

      router.replace({
        pathname: `/signup/${state.navigation.activeStep}`,
        query: filteredQuery,
      });
    })();
  }, [breeds, router]);

  return (
    <Layout
      title={t("signup.meta.title")}
      description={t("signup.meta.description")}
      noRobot
      seoNoIndex
      seoNoFollow
    >
      <div
        style={{
          display: "flex",
          height: "100vh",
        }}
      />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  if (!context.query.breed) {
    return { props: { breeds: [] } };
  }

  const breed = context.query.breed as string;

  const { data } = await client.query<GetBreedsSignupIndex>({
    query: gql`
      query GetBreedsSignupIndex($input: GetBreedsInput) {
        getBreeds(input: $input) {
          name
          id
          lifeAgeGroup
        }
      }
    `,
    fetchPolicy: "network-only",

    variables: {
      input: {
        limit: 100000,
        species: breedToSpecies(breed),
      },
    },
  });

  const breeds = data.getBreeds?.map((b) => ({
    id: b?.id ?? "",
    name: b?.name ?? "",
    lifeAgeGroup: b?.lifeAgeGroup ?? -1,
  }));

  return { props: { breeds } };
};
